function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useMemo, useRef } from 'react';
import { Controller, useFieldArray, useForm, FormProvider } from 'react-hook-form';
import { Button, Callout, FullPageModal, useOpenUnsavedChangesModal } from 'seagull';
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { handlePromise, scrollIntoView } from "../../infra/utils";
import ExternalLinkButton from "../ExternalLinkButton";
import ServicesListStructure from "../ServicesListStructure/ServicesListStructure";
import { EditAllowManualPayments, EditAmendmentsApprovalMethod, EditCCFees, EditInvoicesApprovalMethod, EditLegalTerms, EditNetTerms, Section } from "./EditAgreementComponents";
import { getApprovalCalloutText, makeServiceQboClassChanges, validateService, willEmailBeSent } from "./EditAgreementModal.utils";
import styles from "./EditAgreementModal.module.scss";
import { agreementChangeModel, legalTermsModel, relationshipModel } from "../../models";
import EditAgreementService from "./EditAgreementService";
import { serviceModel } from "../../models";
import ServicePicker from "../ServiceCreation/ServicePicker/ServicePicker";
import { useOnMount } from "../../hooks/useOnMount";
import compact from 'lodash/compact';
import { useUpdateRelationship } from "../../hooks/useUpdateRelationship";
import { makeSentryIgnoredError } from "../ErrorBoundaryPage";
import { AgreementDataModel } from "../../models/AgreementDataModel";
import { helpArticles } from "../../utils/helpArticles";
import { useAppConfig } from "../../store/store-context";
export const autoApprovalHelpUrl = helpArticles.amendmentsRequiringApproval();
export default function EditAgreementModal(props) {
  const {
    relationship,
    dismissModal,
    registerCanDismiss,
    onUpdate
  } = props;
  const {
    logBI,
    fetchLegalTerms,
    fetchQboClasses
  } = useStoreActions();
  const qboClasses = useStore(state => state.qboClasses.data);
  const {
    disableScrollAnimation
  } = useAppConfig();
  const overlayContext = useOverlayContext();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const updateRelationship = useUpdateRelationship();
  const approvalCalloutText = getApprovalCalloutText(relationship.amendmentsApprovalMethod);
  const onClose = () => dismissModal();
  const {
    data: legalTermsDocuments,
    error: legalTermsError
  } = useStore(state => state.legalTerms);
  const servicesRef = useRef({});
  const originalServicesMap = useMemo(() => new Map(relationship.services.map(s => [s.id, s])), [relationship.services]);
  const form = useForm({
    mode: 'all',
    defaultValues: {
      updatedNetTerms: relationshipModel.actualNetTerms(relationship),
      services: relationship.services,
      selectedLegalTermsId: relationship.legalTerms.id,
      amendmentsApprovalMethod: relationshipModel.actualAmendmentsApprovalMethod(relationship),
      invoicesApprovalMethod: relationshipModel.actualInvoicesApprovalMethod(relationship),
      allowManualPayments: relationshipModel.actualAllowManualPayments(relationship),
      coverCreditCardFees: relationshipModel.actualIsVendorCoveringCCFee(relationship)
    }
  });
  const {
    control,
    formState,
    watch,
    setValue,
    handleSubmit
  } = form;
  const {
    append,
    remove,
    update
  } = useFieldArray({
    control,
    name: 'services'
  });
  const [updatedNetTerms, services, selectedLegalTermsId, amendmentsApprovalMethod, invoicesApprovalMethod, allowManualPayments, coverCreditCardFees] = watch(['updatedNetTerms', 'services', 'selectedLegalTermsId', 'amendmentsApprovalMethod', 'invoicesApprovalMethod', 'allowManualPayments', 'coverCreditCardFees']);
  useOnMount(() => {
    fetchLegalTerms();
    fetchQboClasses();
  });
  const submitText = useMemo(() => {
    return willEmailBeSent(services, originalServicesMap, relationship, updatedNetTerms, selectedLegalTermsId, amendmentsApprovalMethod, invoicesApprovalMethod, allowManualPayments, coverCreditCardFees) ? 'Save changes and notify client' : 'Save changes';
  }, [services, originalServicesMap, relationship, updatedNetTerms, selectedLegalTermsId, amendmentsApprovalMethod, invoicesApprovalMethod, allowManualPayments, coverCreditCardFees]);
  const agreementData = AgreementDataModel.fromRelationship(relationship).setNetTerms(updatedNetTerms);
  registerCanDismiss(() => formState.isDirty ? openUnsavedChangesModal({
    testid: 'edit-agr-confirm-cancel'
  }) : Promise.resolve(true));
  function onSubmit() {
    logBI(11001);
    const selectedLegalTerms = legalTermsDocuments === null || legalTermsDocuments === void 0 ? void 0 : legalTermsDocuments.find(_ref => {
      let {
        id
      } = _ref;
      return id === selectedLegalTermsId;
    });
    updateRelationship(_objectSpread(_objectSpread({}, relationship), {}, {
      changes: compact([agreementChangeModel.makeServicesChange(services.map(s => {
        const withChanges = serviceModel.applyChanges(s);
        return serviceModel.toDBService(withChanges, {
          note: serviceModel.note(s)
        });
      })), formState.dirtyFields.amendmentsApprovalMethod && agreementChangeModel.makeAmendmentsApprovalMethodChange(amendmentsApprovalMethod), formState.dirtyFields.invoicesApprovalMethod && agreementChangeModel.makeInvoicesApprovalMethodChange(invoicesApprovalMethod), formState.dirtyFields.updatedNetTerms && agreementChangeModel.makeNetTermsChange(updatedNetTerms), formState.dirtyFields.selectedLegalTermsId && selectedLegalTerms && agreementChangeModel.makeLegalTermsChange(legalTermsModel.toDraftLegalTerms(selectedLegalTerms, false)), formState.dirtyFields.allowManualPayments && agreementChangeModel.makeManualPaymentsChange(allowManualPayments), formState.dirtyFields.coverCreditCardFees && agreementChangeModel.makeCoverCreditCardFeesChange(coverCreditCardFees), ...makeServiceQboClassChanges(services, relationship.services, qboClasses)]),
      onSuccess: async () => {
        await handlePromise(onUpdate());
        dismissModal(undefined, {
          force: true
        });
      }
    }));
  }
  function onServiceChange(service) {
    const idx = services.findIndex(s => s.id === service.id);
    setValue("services.".concat(idx), service, {
      shouldDirty: true,
      shouldValidate: true
    });
    update(idx, service);
  }
  function onRemoveService(serviceId) {
    const idx = services.findIndex(s => s.id === serviceId);
    remove(idx);
  }
  async function onAddService() {
    const newServices = await overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/React.createElement(ServicePicker, _extends({
        agreementData: agreementData
      }, p))
    });
    if (!newServices || newServices.length === 0) {
      return;
    }
    newServices.forEach(s => {
      logBI(11004, {
        id: s.id,
        name: s.name
      });
    });
    append(newServices);
    setTimeout(() => scrollToServiceId(newServices[newServices.length - 1].id));
  }
  function scrollToServiceId(serviceId) {
    const serviceEl = servicesRef.current[serviceId];
    if (serviceEl) {
      scrollIntoView(serviceEl, {
        disableScrollAnimation
      });
    }
  }
  function onInvalidSubmit(errors) {
    if (Array.isArray(errors.services)) {
      const firstServiceIdx = errors.services.findIndex(e => !!e);
      firstServiceIdx > -1 && scrollToServiceId(services[firstServiceIdx].id);
    }
  }
  if (legalTermsError) {
    throw makeSentryIgnoredError('legalTermsError');
  }
  return /*#__PURE__*/React.createElement(FullPageModal, {
    showFooter: true,
    contentClassName: styles.content,
    "data-testid": "edit-agr",
    onClose: onClose
  }, /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement(FullPageModal.HeaderStart, null, /*#__PURE__*/React.createElement("div", {
    "data-testid": "edit-agr-title"
  }, "Edit agreement with ", relationship.customer.name)), /*#__PURE__*/React.createElement("div", {
    className: styles.centeredContent
  }, /*#__PURE__*/React.createElement(Callout, {
    "data-testid": "approval-callout",
    "data-type": approvalCalloutText.type
  }, approvalCalloutText.text, ' ', /*#__PURE__*/React.createElement(ExternalLinkButton, {
    openInNewTab: true,
    underline: "hover",
    href: autoApprovalHelpUrl,
    testid: "approval-help"
  }, "Learn more")), /*#__PURE__*/React.createElement(Section, {
    title: "Services provided"
  }, /*#__PURE__*/React.createElement(ServicesListStructure, {
    addServicesTestid: "add-services",
    onAddService: onAddService
  }, services.map((s, idx) => {
    const originalService = originalServicesMap.get(s.id);
    const serviceAlreadyTerminated = serviceModel.isTerminated(s.status) && !!originalService && serviceModel.isTerminated(originalService.status);
    return !serviceModel.isCanceled(s.status) && !serviceModel.isCompleted(s.status) && !serviceAlreadyTerminated && /*#__PURE__*/React.createElement(Controller, {
      key: s.id,
      control: control,
      name: "services.".concat(idx),
      rules: {
        validate: validateService
      },
      render: _ref2 => {
        let {
          fieldState,
          formState
        } = _ref2;
        return /*#__PURE__*/React.createElement(EditAgreementService, {
          idx: idx,
          service: s,
          ref: el => servicesRef.current[s.id] = el,
          relationship: relationship,
          agreementData: agreementData,
          originalService: originalService,
          showServiceValidationWarning: !!fieldState.error && formState.isSubmitted,
          onServiceChange: onServiceChange,
          onRemoveService: onRemoveService
        });
      }
    });
  }))), /*#__PURE__*/React.createElement(Section, {
    title: "Payment settings"
  }, /*#__PURE__*/React.createElement(EditNetTerms, {
    relationship: relationship
  }), /*#__PURE__*/React.createElement(EditCCFees, {
    relationship: relationship
  }), /*#__PURE__*/React.createElement(EditAllowManualPayments, {
    relationship: relationship
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Agreement terms"
  }, /*#__PURE__*/React.createElement(EditLegalTerms, {
    relationship: relationship,
    legalTermsDocuments: legalTermsDocuments,
    onLegalTermsUpload: fetchLegalTerms
  }), /*#__PURE__*/React.createElement(EditAmendmentsApprovalMethod, {
    relationship: relationship
  }), /*#__PURE__*/React.createElement(EditInvoicesApprovalMethod, {
    relationship: relationship
  }))), /*#__PURE__*/React.createElement(FullPageModal.FooterEnd, null, /*#__PURE__*/React.createElement(Button, {
    skin: "primaryOutline",
    "data-testid": "edit-agr-cancel",
    onClick: onClose
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    disabled: !formState.isDirty,
    "data-testid": "edit-agr-submit",
    onClick: handleSubmit(onSubmit, onInvalidSubmit)
  }, submitText))));
}